/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import apiNew from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import {
 clearAuthTokens,
 clearLastConnectionKey,
 persistAuthTokens,
} from '../../utils/authTokens';
import {
 ForgotPasswordDTO,
 JWTRefreshTokenSubmitDTO,
 JWTTokenDTO,
 LoginTwoFAVerifyDTO,
 ResetPasswordDTO,
 TwoFAVerifyDTO,
 VerifyDTO,
} from './identityTypes';
import {
 identityLoginFailure,
 identityLoginStart,
 identityLoginSuccess,
 identityLogoutStart,
 identityLogoutSuccess,
 identityLogoutFailure,
 identityRefreshTokenStart,
 identityRefreshTokenSuccess,
 identityRefreshTokenFailure,
 identityMicrosoftStart,
 identityMicrosoftSuccess,
 identityMicrosoftFailure,
 identityMicrosoftCallbackStart,
 identityMicrosoftCallbackSuccess,
 identityMicrosoftCallbackFailure,
 identityGoogleStart,
 identityGoogleSuccess,
 identityGoogleFailure,
 identityGoogleCallbackStart,
 identityGoogleCallbackSuccess,
 identityGoogleCallbackFailure,
 identityVerify2FAStart,
 identityVerify2FASuccess,
 identityVerify2FAFailure,
 identityForgotPasswordStart,
 identityForgotPasswordSuccess,
 identityForgotPasswordFailure,
 identityResetPasswordStart,
 identityResetPasswordSuccess,
 identityResetPasswordFailure,
 identityGenerate2FACodeStart,
 identityGenerate2FACodeSuccess,
 identityGenerate2FACodeFailure,
 identitySetVerificationStart,
 identitySetVerificationSuccess,
 identitySetVerificationFailure,
 identityConfirmVerificationStart,
 identityConfirmVerificationSuccess,
 identityConfirmVerificationFailure,
 identitySetTotpStart,
 identitySetTotpSuccess,
 identitySetTotpFailure,
 identityVerifyTotpStart,
 identityVerifyTotpSuccess,
 identityVerifyTotpFailure,
 identityRevokeTokenStart,
 identityRevokeTokenSuccess,
 identityRevokeTokenFailure,
 identityRevokeAllTokensStart,
 identityRevokeAllTokensSuccess,
 identityRevokeAllTokensFailure,
} from './identitySlice';

const deleteUserData = () => {
 localStorage.removeItem('selfUser');
 localStorage.removeItem('accessGroups');
 localStorage.removeItem('userRoles');
};

function* identityLoginSaga(
 props: PayloadAction<{
  email: string;
  password: string;
  connectionKey?: number | null;
 }>,
) {
 try {
  const response: AxiosResponse<JWTTokenDTO> = yield call(
   apiNew.post,
   '/Identity/v1/login',
   {
    Email: props.payload.email,
    Password: props.payload.password,
    ConnectionKey: props.payload.connectionKey ?? null,
   },
  );

  // ############################
  // REFACTOR OTHER LOGIN TYPES SUCCESSES
  // ############################
  deleteUserData();
  persistAuthTokens(response.data);

  yield put(identityLoginSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityLoginFailure);
 }
}

function* identityLogoutSaga() {
 try {
  yield call(apiNew.post, '/Identity/v1/logout', {});

  clearAuthTokens();
  deleteUserData();

  yield put(
   identityLogoutSuccess({
    type: 'success',
    message: 'Sekmingai atsijungta',
   }),
  );
  window.location.href = '/login';
 } catch (error) {
  yield* errorHandling(error, identityLogoutFailure);
 }
}

function* identityRefreshTokenSaga(
 props: PayloadAction<JWTRefreshTokenSubmitDTO>,
) {
 try {
  const payload = {
   access_token: props.payload.access_token,
   refresh_token: props.payload.refresh_token,
   ...(props.payload.connectionKey != null
    ? { connectionKey: props.payload.connectionKey }
    : {}),
  };
  const response: AxiosResponse<JWTTokenDTO> = yield call(
   apiNew.post,
   '/Identity/v1/login/refresh-token',
   payload,
  );
  deleteUserData();
  persistAuthTokens(response.data);

  yield put(identityRefreshTokenSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityRefreshTokenFailure);
 }
}

function* identityMicrosoftSaga(
 props: PayloadAction<{ redirectUri?: string }>,
) {
 try {
  const params = props.payload.redirectUri
   ? `?redirectUri=${encodeURIComponent(props.payload.redirectUri)}`
   : '';
  const response: AxiosResponse<{ uri: string }> = yield call(
   apiNew.get,
   `/Identity/v1/login/microsoft${params}`,
  );
  yield put(identityMicrosoftSuccess({ uri: response.data.uri }));
 } catch (error) {
  yield* errorHandling(error, identityMicrosoftFailure);
 }
}

function* identityMicrosoftCallbackSaga(
 props: PayloadAction<{ code?: string; redirectUri?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.code) params.append('code', props.payload.code);
  if (props.payload.redirectUri)
   params.append('redirectUri', props.payload.redirectUri);
  const queryString = params.toString();
  const url = `/Identity/v1/login/microsoft-callback${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<JWTTokenDTO> = yield call(apiNew.get, url);

  deleteUserData();
  persistAuthTokens(response.data);

  yield put(identityMicrosoftCallbackSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityMicrosoftCallbackFailure);
 }
}

function* identityGoogleSaga(props: PayloadAction<{ redirectUri?: string }>) {
 try {
  const params = props.payload.redirectUri
   ? `?redirectUri=${encodeURIComponent(props.payload.redirectUri)}`
   : '';
  const response: AxiosResponse<{ uri: string }> = yield call(
   apiNew.get,
   `/Identity/v1/login/google${params}`,
  );

  yield put(identityGoogleSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGoogleFailure);
 }
}

function* identityGoogleCallbackSaga(
 props: PayloadAction<{ code?: string; redirectUri?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.code) params.append('code', props.payload.code);
  if (props.payload.redirectUri)
   params.append('redirectUri', props.payload.redirectUri);
  const queryString = params.toString();
  const url = `/Identity/v1/login/google-callback${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<JWTTokenDTO> = yield call(apiNew.get, url);

  deleteUserData();
  persistAuthTokens(response.data);

  yield put(identityGoogleCallbackSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGoogleCallbackFailure);
 }
}

function* identityVerify2FASaga(props: PayloadAction<LoginTwoFAVerifyDTO>) {
 try {
  const response: AxiosResponse<JWTTokenDTO> = yield call(
   apiNew.post,
   '/Identity/v1/login/verify-2fa',
   props.payload,
  );

  deleteUserData();
  persistAuthTokens(response.data);

  yield put(identityVerify2FASuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityVerify2FAFailure);
 }
}

function* identityForgotPasswordSaga(props: PayloadAction<ForgotPasswordDTO>) {
 try {
  const response: AxiosResponse<string> = yield call(
   apiNew.post,
   '/Identity/v1/login/forgot-password',
   {
    Email: props.payload.Email,
    Template: 'ForgotPasswordEmailUI',
   },
  );
  yield put(identityForgotPasswordSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityForgotPasswordFailure);
 }
}

function* identityResetPasswordSaga(props: PayloadAction<ResetPasswordDTO>) {
 try {
  const response: AxiosResponse<string> = yield call(
   apiNew.post,
   '/Identity/v1/login/reset-password',
   props.payload,
  );
  yield put(identityResetPasswordSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityResetPasswordFailure);
 }
}

function* identityGenerate2FACodeSaga(props: PayloadAction<TwoFAVerifyDTO>) {
 try {
  const response: AxiosResponse<string> = yield call(
   apiNew.post,
   '/Identity/v1/login/verify-2fa/generatecode',
   props.payload,
  );
  yield put(identityGenerate2FACodeSuccess({ uri: response.data }));
 } catch (error) {
  yield* errorHandling(error, identityGenerate2FACodeFailure);
 }
}

function* identitySetVerificationSaga(props: PayloadAction<VerifyDTO>) {
 try {
  const response: AxiosResponse<string> = yield call(
   apiNew.post,
   '/Identity/v1/login/setVerification',
   props.payload,
  );
  yield put(identitySetVerificationSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identitySetVerificationFailure);
 }
}

function* identityConfirmVerificationSaga(
 props: PayloadAction<{
  sessionId?: string;
  userid?: string;
  method?: 1 | 2 | 3;
  code?: string;
 }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.sessionId)
   params.append('sessionId', props.payload.sessionId);
  if (props.payload.userid) params.append('userid', props.payload.userid);
  if (props.payload.method)
   params.append('method', props.payload.method.toString());
  if (props.payload.code) params.append('code', props.payload.code);
  const queryString = params.toString();
  const url = `/Identity/v1/login/confirmVerification${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<string> = yield call(apiNew.get, url);
  yield put(identityConfirmVerificationSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityConfirmVerificationFailure);
 }
}

function* identitySetTotpSaga(
 props: PayloadAction<{ userid?: string; sessionId?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.userid) params.append('userid', props.payload.userid);
  if (props.payload.sessionId)
   params.append('sessionId', props.payload.sessionId);
  const queryString = params.toString();
  const url = `/Identity/v1/login/setTotp${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<{ uri: string }> = yield call(apiNew.get, url);
  yield put(identitySetTotpSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identitySetTotpFailure);
 }
}

function* identityVerifyTotpSaga(
 props: PayloadAction<{ userid?: string; sessionId?: string; code?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.userid) params.append('userid', props.payload.userid);
  if (props.payload.sessionId)
   params.append('sessionId', props.payload.sessionId);
  if (props.payload.code) params.append('code', props.payload.code);
  const queryString = params.toString();
  const url = `/Identity/v1/login/verifyTotp${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<string> = yield call(apiNew.post, url, {});
  yield put(identityVerifyTotpSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityVerifyTotpFailure);
 }
}

function* identityRevokeTokenSaga(props: PayloadAction<{ Id: string }>) {
 try {
  yield call(apiNew.post, `/Identity/v1/revoke/${props.payload.Id}`, {});
  yield put(identityRevokeTokenSuccess());
 } catch (error) {
  yield* errorHandling(error, identityRevokeTokenFailure);
 }
}

function* identityRevokeAllTokensSaga() {
 try {
  yield call(apiNew.post, '/Identity/v1/revoke-all', {});
  yield put(identityRevokeAllTokensSuccess());
 } catch (error) {
  yield* errorHandling(error, identityRevokeAllTokensFailure);
 }
}

export function* watchIdentitySaga() {
 yield takeLatest(identityLoginStart.type, identityLoginSaga);
 yield takeLatest(identityLogoutStart.type, identityLogoutSaga);
 yield takeLatest(identityRefreshTokenStart.type, identityRefreshTokenSaga);
 yield takeLatest(identityMicrosoftStart.type, identityMicrosoftSaga);
 yield takeLatest(
  identityMicrosoftCallbackStart.type,
  identityMicrosoftCallbackSaga,
 );
 yield takeLatest(identityGoogleStart.type, identityGoogleSaga);
 yield takeLatest(identityGoogleCallbackStart.type, identityGoogleCallbackSaga);
 yield takeLatest(identityVerify2FAStart.type, identityVerify2FASaga);
 yield takeLatest(identityForgotPasswordStart.type, identityForgotPasswordSaga);
 yield takeLatest(identityResetPasswordStart.type, identityResetPasswordSaga);
 yield takeLatest(
  identityGenerate2FACodeStart.type,
  identityGenerate2FACodeSaga,
 );
 yield takeLatest(
  identitySetVerificationStart.type,
  identitySetVerificationSaga,
 );
 yield takeLatest(
  identityConfirmVerificationStart.type,
  identityConfirmVerificationSaga,
 );
 yield takeLatest(identitySetTotpStart.type, identitySetTotpSaga);
 yield takeLatest(identityVerifyTotpStart.type, identityVerifyTotpSaga);
 yield takeLatest(identityRevokeTokenStart.type, identityRevokeTokenSaga);
 yield takeLatest(
  identityRevokeAllTokensStart.type,
  identityRevokeAllTokensSaga,
 );
}

